import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItemButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Toast, type ToastMessage } from '../components/Toast';
import { formatShort } from '../lib/dates';

/**
 * SMS.
 *
 * Sends through Cellcast. The credential is a Worker secret — this screen can
 * tell you whether one is configured and nothing more. The legacy app kept it
 * in a database table in plaintext.
 */

interface StaffSummary {
    readonly staffId: number;
    readonly name: string;
}

interface Recipient {
    readonly staffId: number | null;
    readonly staffName: string | null;
    readonly phoneNumber: string | null;
    readonly deliveryStatus: string;
    readonly error: string | null;
}

interface Message {
    readonly id: number;
    readonly body: string;
    readonly sentAt: string;
    readonly sentByName: string | null;
    readonly error: string | null;
    readonly recipients: readonly Recipient[];
}

/** Below this many credits the figure turns amber, so a top-up is not left until sends fail. */
const LOW_BALANCE = 100;

type Status = 'all' | 'sent' | 'failed';

const STATUS_FILTERS: readonly { readonly value: Status; readonly label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' },
];

/** A message failed if the send did, or if any one recipient did. */
function hasFailure(m: Message): boolean {
    return m.error !== null || m.recipients.some((r) => r.deliveryStatus === 'failed');
}

/** Matches the server's segmentCount — shown before sending, since it is billed. */
function segments(body: string): number {
    if (body.length === 0) {
        return 0;
    }
    return body.length <= 160 ? 1 : Math.ceil(body.length / 153);
}

export function Sms(): React.JSX.Element {
    const [staff, setStaff] = useState<readonly StaffSummary[]>([]);
    const [messages, setMessages] = useState<readonly Message[]>([]);
    const [configured, setConfigured] = useState<boolean | null>(null);
    const [senderId, setSenderId] = useState('Silverline');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [nonce, setNonce] = useState(0);
    const [balance, setBalance] = useState<number | null>(null);
    const [composing, setComposing] = useState(false);
    const [status, setStatus] = useState<Status>('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        Promise.all([
            apiFetch<{ configured: boolean; senderId: string }>('/sms/config'),
            apiFetch<{ staff: readonly StaffSummary[] }>('/staff'),
        ])
            .then(([c, s]) => {
                setConfigured(c.configured);
                setSenderId(c.senderId);
                setStaff(s.staff);
            })
            .catch(() => {
                setConfigured(false);
            });
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch<{ messages: readonly Message[] }>('/sms?limit=50')
            .then((r) => {
                if (!cancelled) {
                    setMessages(r.messages);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load messages');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    // Re-read after every send (nonce), since each one spends credits.
    useEffect(() => {
        if (configured !== true) {
            return;
        }
        let cancelled = false;
        apiFetch<{ balance: number | null }>('/sms/balance')
            .then((r) => {
                if (!cancelled) {
                    setBalance(r.balance);
                }
            })
            .catch(() => {
                /* The figure stays hidden; sending still works. */
            });
        return () => {
            cancelled = true;
        };
    }, [configured, nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    const counts = useMemo(() => {
        const failed = messages.filter(hasFailure).length;
        return { all: messages.length, sent: messages.length - failed, failed } satisfies Record<Status, number>;
    }, [messages]);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return messages.filter(
            (m) =>
                (status === 'all' || (status === 'failed') === hasFailure(m)) &&
                (q === '' ||
                    m.body.toLowerCase().includes(q) ||
                    m.recipients.some((r) => (r.staffName ?? r.phoneNumber ?? '').toLowerCase().includes(q))),
        );
    }, [messages, status, search]);

    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="SMS" subtitle="Text staff from the office number" />

            {configured === false ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    SMS is not configured on this environment. Set the Cellcast key with{' '}
                    <code>wrangler secret put CELLCAST_API_KEY</code>. It is never stored in the database — the legacy
                    app kept it in a table in plaintext, and that key should be rotated.
                </Alert>
            ) : null}

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flexWrap="wrap"
                        sx={{ flex: 1 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search message or recipient..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            slotProps={{
                                input: { startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> },
                            }}
                            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 360 } }}
                        />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            {STATUS_FILTERS.map((f) => (
                                <Button
                                    key={f.value}
                                    size="small"
                                    variant={status === f.value ? 'contained' : 'outlined'}
                                    color={status === f.value ? 'primary' : 'inherit'}
                                    onClick={() => {
                                        setStatus(f.value);
                                        setPage(0);
                                    }}
                                    sx={{ flex: { xs: '1 1 30%', sm: '0 0 auto' } }}
                                >
                                    {f.label}
                                    {loading ? '' : ` (${String(counts[f.value])})`}
                                </Button>
                            ))}
                        </Stack>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                        {balance !== null ? (
                            <Tooltip title="SMS credits left on the Cellcast account">
                                <Chip
                                    variant="outlined"
                                    color={balance < LOW_BALANCE ? 'warning' : 'default'}
                                    icon={<AccountBalanceWalletOutlinedIcon />}
                                    label={`${balance.toLocaleString('en-AU', { maximumFractionDigits: 2 })} credits`}
                                    sx={{ fontWeight: 600 }}
                                />
                            </Tooltip>
                        ) : null}
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            disabled={configured !== true}
                            onClick={() => {
                                setComposing(true);
                            }}
                        >
                            New SMS
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 820 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Message</TableCell>
                            <TableCell>Sent</TableCell>
                            <TableCell>To</TableCell>
                            <TableCell>Segments</TableCell>
                            <TableCell>Sent By</TableCell>
                            <TableCell align="right">Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {loading
                                            ? 'Loading messages...'
                                            : search.trim() === ''
                                              ? 'Nothing sent yet.'
                                              : `No messages match "${search}".`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageRows.map((m, idx) => {
                                const failed = hasFailure(m);
                                const parts = segments(m.body);
                                return (
                                    <TableRow
                                        key={m.id}
                                        hover
                                        sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}
                                    >
                                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                                            {m.body}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {formatShort(m.sentAt.slice(0, 10))}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                {m.recipients.map((r, i) => (
                                                    <Tooltip
                                                        key={`${String(r.staffId)}-${String(i)}`}
                                                        title={r.error ?? r.phoneNumber ?? ''}
                                                    >
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            color={r.deliveryStatus === 'failed' ? 'error' : 'default'}
                                                            label={r.staffName ?? r.phoneNumber ?? 'Unknown'}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Segments × recipients — what Cellcast bills">
                                                <span>
                                                    {parts} × {m.recipients.length} = {parts * m.recipients.length}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{m.sentByName ?? '—'}</TableCell>
                                        <TableCell align="right">
                                            {failed ? (
                                                <Tooltip title={m.error ?? 'One or more recipients failed'}>
                                                    <Chip
                                                        size="small"
                                                        color="error"
                                                        icon={<WarningAmberIcon />}
                                                        label="Failed"
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    icon={<CheckCircleOutlineIcon />}
                                                    label="Sent"
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rows.length}
                page={currentPage}
                onPageChange={(_e, p) => {
                    setPage(p);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            <ComposeDialog
                open={composing}
                staff={staff}
                senderId={senderId}
                onClose={() => {
                    setComposing(false);
                }}
                onSent={(result) => {
                    setComposing(false);
                    setToast(result);
                    reload();
                }}
            />

            <Toast
                toast={toast}
                autoHideDuration={toast?.severity === 'warning' ? 10000 : 4000}
                onClose={(reason) => {
                    if (reason !== 'clickaway') {
                        setToast(null);
                    }
                }}
            />
        </Container>
    );
}

// ─── Dialog ─────────────────────────────────────────────────────────────────

interface ComposeDialogProps {
    readonly open: boolean;
    readonly staff: readonly StaffSummary[];
    readonly senderId: string;
    readonly onClose: () => void;
    readonly onSent: (result: ToastMessage) => void;
}

function ComposeDialog({ open, staff, senderId, onClose, onSent }: ComposeDialogProps): React.JSX.Element {
    const [body, setBody] = useState('');
    const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
    const [search, setSearch] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        setBody('');
        setSelected(new Set());
        setSearch('');
        setError(null);
    }, [open]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q === '' ? staff : staff.filter((s) => s.name.toLowerCase().includes(q));
    }, [staff, search]);

    function send(): void {
        setSending(true);
        setError(null);
        apiFetch<{ sent: number; skipped: { staffId: number; reason: string }[] }>('/sms', {
            method: 'POST',
            body: JSON.stringify({ body, staffIds: [...selected] }),
        })
            .then((r) => {
                if (r.skipped.length === 0) {
                    onSent({ message: `Sent to ${String(r.sent)}`, severity: 'success' });
                    return;
                }
                const names = r.skipped
                    .map((s) => `${staff.find((x) => x.staffId === s.staffId)?.name ?? s.staffId} (${s.reason})`)
                    .join(', ');
                onSent({
                    message: `Sent to ${String(r.sent)} — ${String(r.skipped.length)} skipped: ${names}`,
                    severity: 'warning',
                });
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not send');
            })
            .finally(() => {
                setSending(false);
            });
    }

    const parts = segments(body);

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>New SMS</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    <Box>
                        <TextField
                            label="Message"
                            multiline
                            minRows={4}
                            fullWidth
                            autoFocus
                            value={body}
                            onChange={(e) => {
                                setBody(e.target.value);
                            }}
                        />
                        <Stack direction="row" gap={1.5} sx={{ mt: 0.75 }}>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                                From <strong>{senderId}</strong>
                            </Typography>
                            {/* Cellcast bills per segment per recipient, so the
                                cost of a long message is shown before sending. */}
                            <Typography
                                sx={{ fontSize: '0.75rem', color: parts > 1 ? 'warning.main' : 'text.secondary' }}
                            >
                                {body.length} chars · {parts} segment{parts === 1 ? '' : 's'}
                                {selected.size > 0
                                    ? ` × ${String(selected.size)} = ${String(parts * selected.size)}`
                                    : ''}
                            </Typography>
                        </Stack>
                    </Box>

                    <Alert severity="info">
                        Never send passwords by SMS — staff set their own through the invite email.
                    </Alert>

                    <Box>
                        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, flex: 1 }}>
                                To ({selected.size})
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => {
                                    setSelected(
                                        selected.size === filtered.length
                                            ? new Set()
                                            : new Set(filtered.map((s) => s.staffId)),
                                    );
                                }}
                            >
                                {selected.size === filtered.length && filtered.length > 0 ? 'None' : 'All'}
                            </Button>
                        </Stack>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search staff"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                        />
                        <Divider sx={{ my: 1 }} />
                        <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                            {filtered.map((s) => (
                                <ListItemButton
                                    key={s.staffId}
                                    onClick={() => {
                                        setSelected((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(s.staffId)) {
                                                next.delete(s.staffId);
                                            } else {
                                                next.add(s.staffId);
                                            }
                                            return next;
                                        });
                                    }}
                                    sx={{ borderRadius: 1, py: 0 }}
                                >
                                    <Checkbox
                                        edge="start"
                                        size="small"
                                        checked={selected.has(s.staffId)}
                                        tabIndex={-1}
                                    />
                                    <Typography noWrap sx={{ fontSize: '0.82rem' }}>
                                        {s.name}
                                    </Typography>
                                </ListItemButton>
                            ))}
                        </List>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>
                            Staff without a mobile on file are reported after sending, not skipped silently.
                        </Typography>
                    </Box>

                    {error !== null ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={send}
                    disabled={sending || body.trim() === '' || selected.size === 0}
                >
                    {sending ? 'Sending...' : `Send to ${String(selected.size)}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
